import React, { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { ukDayYear } from "@/lib/time";
import { gbp } from "@/lib/stages";
import {
  Empty, KeyValue, Note, PageHead, Panel, RailCard,
  DeskSupport, StatStrip,
} from "@/components/portal/crm/kit";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

const connexisFee = (me, lead) => {
  const type = me?.commission_type || "percentage";
  if (type === "flat") return Number(me?.commission_flat_gbp || 0);
  if (type === "percentage") return (Number(lead.amount_gbp || 0) * Number(me?.commission_pct || 0)) / 100;
  return null; // income share — we can't know it until the desk tells us what it earned
};

export default function Fees() {
  const { leads, me } = useOutletContext();
  const funded = useMemo(() => leads.filter((l) => l.status === "completed"), [leads]);
  const incomeShare = (me?.commission_type || "percentage") === "income_share";

  const billed = funded.reduce((n, l) => n + (connexisFee(me, l) || 0), 0);
  const fundedValue = funded.reduce((n, l) => n + Number(l.amount_gbp || 0), 0);

  return (
    <>
      <PageHead
        eyebrow="What we invoice, and why"
        title="Completion fees"
        lede={incomeShare
          ? "You pay us a share of what you earn, so every funded case needs one figure from you — what the deal actually paid your desk."
          : "Every funded case and the fee it generates under your agreement. Nothing here is a surprise at month end."}
        testId="lender-fees-head"
      />

      <StatStrip
        testId="lender-fees-stats"
        items={[
          { k: "Funded through us", v: funded.length, tone: "green", h: "Completions on your desk", testId: "lender-fees-count" },
          { k: "Facilities written", v: gbp(fundedValue), h: "Total funded value", testId: "lender-fees-value" },
          { k: incomeShare ? "Awaiting your figure" : "Fees due to Connexis", v: incomeShare ? funded.filter((l) => !l.income_confirmed_at).length : gbp(billed), h: incomeShare ? "Cases we cannot invoice yet" : "Across every funded case", testId: "lender-fees-due" },
          { k: "Payment terms", v: `${me?.payment_terms_days ?? 14} days`, h: "From the date we invoice", testId: "lender-fees-terms" },
        ]}
      />

      <div className="grid items-start gap-7 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-7">
          <Panel title="Funded cases" sub={me?.terms_short || "Your agreed fee model"} pad={false} testId="lender-fees-table">
            {funded.length === 0 ? (
              <Empty testId="lender-fees-empty">Nothing funded through Connexis yet. This fills in as cases complete.</Empty>
            ) : (
              <div className="divide-y divide-slate-100">
                {funded.map((l) => (
                  <FeeRow key={l.id} lead={l} me={me} incomeShare={incomeShare} />
                ))}
              </div>
            )}
          </Panel>

          <Panel title="How the fee is worked out" testId="lender-fees-method">
            <ol className="space-y-4">
              {(incomeShare
                ? [
                  ["You fund the deal", "The client draws the facility and your desk earns its fee."],
                  ["You tell us the figure", "One number: what this deal paid you. It is the only input we need."],
                  ["We invoice our share", `${me?.commission_pct ?? 0}% of that figure, on ${me?.payment_terms_days ?? 14}-day terms.`],
                  ["The introducer is paid from our share", "Never from yours, and never out of the client's facility."],
                ]
                : [
                  ["You fund the deal", "The client draws the facility."],
                  ["We invoice", me?.commission_type === "flat"
                    ? `${gbp(me?.commission_flat_gbp)} per funded case, on ${me?.payment_terms_days ?? 14}-day terms.`
                    : `${me?.commission_pct ?? 0}% of the funded amount, on ${me?.payment_terms_days ?? 14}-day terms.`],
                  ["The introducer is paid from our fee", "Never from yours, and never out of the client's facility."],
                  ["It appears on your statement", "With the case reference, so you can tie it back."],
                ]
              ).map(([t, b], i) => (
                <li key={t} className="flex gap-4">
                  <span className="grid size-7 shrink-0 place-items-center rounded-[9px] bg-[#E4ECFF] font-archivo text-[12.5px] font-extrabold text-[#0A3DB8]">{i + 1}</span>
                  <div>
                    <div className="font-archivo text-[14px] font-bold text-[#0B1220]">{t}</div>
                    <div className="mt-1 text-[12.5px] leading-relaxed text-slate-500">{b}</div>
                  </div>
                </li>
              ))}
            </ol>
          </Panel>
        </div>

        <div className="space-y-4">
          <RailCard title="Your agreement" testId="lender-fees-agreement">
            <KeyValue label="Model" value={me?.terms_short || "—"} />
            {incomeShare && me?.expected_fee_pct != null && <KeyValue label="Forecast fee" value={`${me.expected_fee_pct}% of facility`} />}
            <KeyValue label="Terms" value={`${me?.payment_terms_days ?? 14} days`} />
            <KeyValue label="Signed" value={me?.agreement?.status === "signed" ? "Yes" : "Not yet"} tone={me?.agreement?.status === "signed" ? "green" : undefined} />
          </RailCard>

          <DeskSupport body="Query an invoice, or tell us a deal fell away after completion — we would rather correct it than chase it." />
          <Note>
            {incomeShare
              ? "Your forecast fee percentage is only used to size a case before it funds. What you confirm here is what we invoice."
              : "A case that unwinds after completion is credited in full. Tell the desk and we'll reverse it."}
          </Note>
        </div>
      </div>
    </>
  );
}

function FeeRow({ lead, me, incomeShare }) {
  const [income, setIncome] = useState(lead.partner_income_gbp ?? "");
  const [saved, setSaved] = useState(lead.income_confirmed_at || null);
  const [fee, setFee] = useState(null);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    const v = parseFloat(income);
    if (!(v > 0)) { toast.error("Tell us the figure you earned on this deal"); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/lender/leads/${lead.id}/income`, { income_gbp: v });
      setFee(r.data.connexis_fee_gbp);
      setSaved(new Date().toISOString());
      toast.success("Thank you — that is the figure we invoice against");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save that figure"); }
    finally { setBusy(false); }
  };

  const flatFee = connexisFee(me, lead);

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-4" data-testid={`lender-fee-row-${lead.id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-archivo text-[14px] font-bold text-[#0B1220] truncate">{lead.business_name || lead.applicant_name}</div>
        <div className="mt-0.5 text-[12px] text-slate-500">
          {lead.finance_type} · {gbp(lead.amount_gbp)} facility · funded {ukDayYear(lead.status_updated_at, "—")}
        </div>
      </div>

      {incomeShare ? (
        saved ? (
          <div className="flex shrink-0 items-center gap-8">
            <Figure label="You earned" value={<span className="flex items-center gap-1.5 text-[#0B6B4F]"><Check className="size-3.5" />{gbp(income)}</span>} />
            <Figure label="Our share" value={fee != null ? gbp(fee) : "Confirmed"} muted={fee == null} />
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-2">
            <div className="relative w-[150px]">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-slate-400">£</span>
              <input
                inputMode="numeric"
                value={income ? Number(String(income).replace(/[^0-9]/g, "")).toLocaleString() : ""}
                placeholder="what it paid you"
                onChange={(e) => { const d = e.target.value.replace(/[^0-9]/g, ""); setIncome(d ? String(parseInt(d, 10)) : ""); }}
                className="h-10 w-full rounded-[10px] border border-slate-200 bg-white pl-7 pr-3 text-[14px] font-semibold tabular-nums text-slate-900 outline-none placeholder:text-[11.5px] placeholder:font-normal placeholder:text-slate-400 focus:border-[#0055FF]"
                data-testid={`lender-income-${lead.id}`}
              />
            </div>
            <Button onClick={confirm} disabled={busy} className="h-10 shrink-0 rounded-[10px] bg-[#0055FF] px-4 text-[12.5px] font-bold hover:bg-[#0044CC]" data-testid={`lender-income-save-${lead.id}`}>
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : "Confirm"}
            </Button>
          </div>
        )
      ) : (
        <div className="flex shrink-0 items-center gap-8">
          <Figure label="Our fee" value={gbp(flatFee)} />
          <span className="text-[11.5px] font-semibold text-slate-400">invoiced on completion</span>
        </div>
      )}
    </div>
  );
}

/** A small right-aligned label + figure, so each row reads itself without column headers. */
function Figure({ label, value, muted }) {
  return (
    <div className="text-right">
      <div className="text-[9.5px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</div>
      <div className={`mt-0.5 text-[14px] font-bold tabular-nums ${muted ? "text-slate-400" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
